import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { CalendarEventForm } from "./CalendarEventForm";
import { deleteCalendarItem, getCalendarItems, postCalendarItem, putCalendarItem } from "../../api/calendarApi";
import { deleteTaskItem, getTaskItems, postTaskItem, putTaskItem } from "../../api/taskApi";
import './CalendarGrid.css';
import { formatDateMonthLocalized, getLocalizedShortDays, getTimezoneAwareMinutes, getTimezoneAwareHours, getTimezoneAwareDate, getTimezoneAwareMonth, getTimezoneAwareYear, getTimezoneAwareDay, setTimezoneAwareHours } from "../../utils/dateFormats";
import { ITicket } from "../../types/dashboard";
import { DashboardContext } from "../../pages/dashboardPage/DashboardContext";
import { AppContext } from "../../appContext";
import { CalendarViewSwitcher, CalendarViewType } from "./CalendarViewSwitcher";
import { CalendarGridDay } from "./CalendarGridDay";
import { CalendarGridWeek } from "./CalendarGridWeek";
import { CalendarGridMonth } from "./CalendarGridMonth";
import { CalendarGridYear } from "./CalendarGridYear";
import { addCalendarReloadListener, removeCalendarReloadListener, addTaskReloadListener, removeTaskReloadListener, triggerTaskReload, triggerCalendarReload } from "../../utils/dataSyncTriggers";

interface ICalendarGridProps{
    tickets?: ITicket[]
}

const CalendarTicket = ({ticket, ticketIndex, totalTickets, onTicketClick}: {ticket: ITicket, ticketIndex?: number, totalTickets?: number, onTicketClick: ()=>void}) => {
    const getTicketColor = (type: string) => {
        switch(type) {
            case 'session': return 'calendarGrid_ticket--colorA';
            case 'event': return 'calendarGrid_ticket--colorB';
            case 'task': return 'calendarGrid_ticket--colorC';
            default: return 'calendarGrid_ticket--colorA';
        }
    };

    // Calculate positioning for multiple tickets - maintain consistent height
    const ticketHeight = 25; // Fixed height for all tickets
    const topOffset = ticketIndex !== undefined && totalTickets && totalTickets > 1
        ? 2 + (ticketIndex * (ticketHeight + 2)) // 2px gap between stacked events
        : 2;

    return (
        <div
            className={`calendarGrid_ticket ${getTicketColor(ticket.type)}`}
            onClick={onTicketClick}
            style={{
                "--offsetTime": `${getTimezoneAwareMinutes(ticket.start) / 60 * 100}%`,
                "--duration": `${(ticket.end.valueOf() - ticket.start.valueOf()) / (1000 * 60 * 60) * 100}%`,
                top: `${topOffset}px`,
                height: `${ticketHeight}px`,
                minHeight: `${ticketHeight}px`
            } as any}
        >
            <div className="calendarGrid_ticket_content">
                <div className="calendarGrid_ticket_time">
                    {getTimezoneAwareHours(ticket.start).toString().padStart(2, '0')}:
                    {getTimezoneAwareMinutes(ticket.start).toString().padStart(2, '0')}
                </div>
                <div className="calendarGrid_ticket_title">
                    {ticket.title || 'New Event'}
                </div>
            </div>
        </div>
    );
};

export const CalendarGrid = ({tickets}: ICalendarGridProps)=>{
    const [baseDate, setBaseDate] = useState(new Date());
    const [currentView, setCurrentView] = useState<CalendarViewType>('month');
    const [createEventTime, setCreateEventTime] = useState(null);
    const [editTicket, setEditTicket] = useState(null);
    const [createdTickets, setCreatedTickets] = useState<Array<ITicket>>([]);
    const [createdTaskTickets, setTaskTickets] = useState<Array<ITicket>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { translate } = useContext(AppContext);
    const {editSession, deleteSession} = useContext(DashboardContext);

    const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
    
    const serverToClientEvent = useCallback((it: any): ITicket | null => {
        try {
            const id = Object.keys(it)[0];
            if (reservedKeys.includes(id)) return null;
            const data = it[id];
            if (!data || typeof data !== 'object') return null;
            const restoredDate = new Date(typeof data.Datetime == 'number' ? (data.Datetime * 1000) : (data.Datetime || data.start));
            if (Number.isNaN(restoredDate.valueOf())){
                console.warn('Skipping item with invalid date:', id, data);
                return null;
            }
            const diffDate = new Date(data.end).valueOf() - new Date(data.start).valueOf();
            const restoredDateTo = new Date(restoredDate.valueOf() + (isNaN(diffDate) ? 0 : diffDate));

            return {
                ...it[id],
                end: restoredDateTo,
                start: restoredDate,
                id,
                original: {
                    ...data
                }
            }
        } catch (error) {
            console.warn('Error parsing calendar item:', error, it);
            return null;
        }
    }, []);

    const normalizeApiResponse = useCallback((data: any): any[] => {
        if (Array.isArray(data)) {
            return data;
        }
        if (data && typeof data === 'object') {
            return Object.entries(data)
                .filter(([id]) => !reservedKeys.includes(id))
                .map(([id, itemData]) => ({ [id]: itemData }));
        }
        return [];
    }, []);

    const loadAllItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const [calendarRes, taskRes] = await Promise.all([
                getCalendarItems().then(res=>res.json()),
                getTaskItems().then(res=>res.json())
            ]);
            
            const calendarArray = normalizeApiResponse(calendarRes);
            const taskArray = normalizeApiResponse(taskRes);
            
            const convertedCalendar = calendarArray.map((it: any)=> serverToClientEvent(it)).filter((it: any) => it !== null);
            const convertedTasks = taskArray.map((it: any)=> serverToClientEvent(it)).filter((it: any) => it !== null);
            
            setCreatedTickets(convertedCalendar.filter((it: any)=>it.type == 'event'));
            setTaskTickets(convertedTasks.filter((it: any)=>it.type == 'task'));
        } catch (error) {
            console.error('Failed to load calendar items:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serverToClientEvent, normalizeApiResponse]);

    useEffect(()=>{
        loadAllItems();
    }, [loadAllItems]);

    useEffect(() => {
        addCalendarReloadListener(loadAllItems);
        addTaskReloadListener(loadAllItems);
        
        return () => {
            removeCalendarReloadListener(loadAllItems);
            removeTaskReloadListener(loadAllItems);
        };
    }, [loadAllItems]);

    const loadCalendarItems = ()=>{
        getCalendarItems().then(res=>res.json()).then(res=>{
            const dataArray = normalizeApiResponse(res);
            const converted = dataArray.map((it: any)=>{
                return serverToClientEvent(it);
            }).filter((it: any) => it !== null);
            setCreatedTickets(converted.filter((it: any)=>it.type == 'event'));
        });
    }

    const loadTaskItems = ()=>{
        getTaskItems().then(res=>res.json()).then(res=>{
            const dataArray = normalizeApiResponse(res);
            const converted = dataArray.map((it: any)=>{
                return serverToClientEvent(it);
            }).filter((it: any) => it !== null);
            setTaskTickets(converted.filter((it: any)=>it.type == 'task'));
        });
    }

    const ticketToServer = (data: ITicket) => {
        const serverFormat = {
            ...data,
            Datetime: Math.floor(data.start.valueOf() / 1000),
            ...data.original.repeatParams
        }
        delete serverFormat.original;
        delete serverFormat.start;
        delete serverFormat.end;
        // Keep editOtherEvents as backend expects it in the JSON body
        return serverFormat;
    }

    const createCalandarItem = (data: ITicket)=>{
        const serverFormat = ticketToServer(data);
        postCalendarItem(serverFormat).then(res=>res.json).then(res=>{
            triggerCalendarReload();
        });
    }

    const editCalandarItem = (data: ITicket)=>{
        const serverFormat = ticketToServer(data);
        const editOtherEvents = data.editOtherEvents || false;
        putCalendarItem(serverFormat, data.id, editOtherEvents).then(res=>res.json()).then(res=>{
            triggerCalendarReload();
        });
    }

    const _deleteCaledarItem = (data: ITicket, all?: boolean)=>{
        deleteCalendarItem(data.id, all).then(res=>res.json()).then(res=>{
            triggerCalendarReload();
        });
    }

    const createTaskItem = (data: ITicket)=>{
        const serverFormat = ticketToServer(data);
        postTaskItem(serverFormat).then(res=>res.json).then(res=>{
            triggerTaskReload();
        });
    }

    const editTaskItem = (data: ITicket)=>{
        const serverFormat = ticketToServer(data);
        const editOtherEvents = data.editOtherEvents || false;
        putTaskItem(serverFormat, data.id, editOtherEvents).then(res=>res.json).then(res=>{
            triggerTaskReload();
        });
    }

    const _deletTaskItem = (data: ITicket, all?: boolean)=>{
        deleteTaskItem(data.id, all).then(res=>res.json()).then(res=>{
            triggerTaskReload();
        });
    }

    const changeDate = (direction: number)=>{
        setBaseDate((last)=>{
            const newDate = new Date(last);
            const nextMonth = newDate.getUTCMonth() + 1 * direction;
            if (nextMonth == 12){
                newDate.setUTCMonth(0);
                newDate.setUTCFullYear(newDate.getUTCFullYear() + 1);
            } else if (nextMonth == -1){
                newDate.setUTCMonth(11);
                newDate.setUTCFullYear(newDate.getUTCFullYear() - 1);
            } else {
                newDate.setUTCMonth(nextMonth)
            }
            return newDate
        })
    }

    // Get days in current month
    const getDaysInMonth = (date: Date) => {
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        // Get the actual number of days in this specific month/year
        const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
        const days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            const day = new Date(Date.UTC(year, month, i));
            days.push(day);
        }
        return days;
    };

    const daysInMonth = getDaysInMonth(baseDate);
    const timeSlots = Array.from({length: 24}, (_, i) => i);
    const allTickets = useMemo(()=>[...tickets || [], ...createdTickets, ...createdTaskTickets], [tickets, createdTickets, createdTaskTickets]);
    const localizedShortDays = getLocalizedShortDays(translate);

    const handleTimeClick = (day: Date, hour: number) => {
        const clickTime = setTimezoneAwareHours(day, hour, 0, 0, 0);
        setCreateEventTime(clickTime);
    };

    const getTicketsForDayAndHour = (day: Date, hour: number) => {
        const startTime = setTimezoneAwareHours(day, hour, 0, 0, 0);
        const endTime = setTimezoneAwareHours(day, hour + 1, 0, 0, 0);

        return allTickets.filter(ticket => {
            return ticket.start >= startTime && ticket.start < endTime &&
                   getTimezoneAwareDate(ticket.start) === getTimezoneAwareDate(day) &&
                   getTimezoneAwareMonth(ticket.start) === getTimezoneAwareMonth(day) &&
                   getTimezoneAwareYear(ticket.start) === getTimezoneAwareYear(day);
        });
    };

    const handleDelete = (data: ITicket, all: boolean)=>{
        if (editTicket){
            if (data.type == 'session'){
                setCreateEventTime(null);
                setEditTicket(null);
                deleteSession(data.original)
            }
            if (data.type == 'event'){
                setCreateEventTime(null);
                setEditTicket(null);
                setCreatedTickets(last=>{
                    const next = [...last];
                    const fountIndex = next.findIndex(it=>editTicket == it);
                    next.splice(fountIndex, 1);
                    return next;
                });
                _deleteCaledarItem(data, all);
            }
            if (data.type == 'task'){
                setCreateEventTime(null);
                setEditTicket(null);
                setTaskTickets(last=>{
                    const next = [...last];
                    const fountIndex = next.findIndex(it=>editTicket == it);
                    next.splice(fountIndex, 1);
                    return next;
                });
                _deletTaskItem(data, all);
            }
        }
        if (createEventTime && !editTicket){
            setCreateEventTime(null)
        }
    }

    const handleSave = (data: ITicket) => {
        if (editTicket){
            if (data.type == 'session'){
                setCreateEventTime(null);
                setEditTicket(null);
                editSession(data.original);
            }
            if (data.type == 'event'){
                setCreateEventTime(null);
                setEditTicket(null);
                setCreatedTickets(last=>{
                    const next = [...last];
                    const fountIndex = next.findIndex(it=>editTicket == it);
                    next.splice(fountIndex, 1, data);
                    return next;
                });
                editCalandarItem(data)
            }
            if (data.type == 'task'){
                setCreateEventTime(null);
                setEditTicket(null);
                setTaskTickets(last=>{
                    const next = [...last];
                    const fountIndex = next.findIndex(it=>editTicket == it);
                    next.splice(fountIndex, 1, data);
                    return next;
                });
                editTaskItem(data)
            }
        }

        if (createEventTime && !editTicket){
            if (data.type == 'session'){
                setCreateEventTime(null);
                setEditTicket(null);
                editSession(data.original);
            }
            if (data.type == 'event'){
                setCreateEventTime(null);
                setCreatedTickets(last=> [...last, data]);
                createCalandarItem(data);
            }
            if (data.type == 'task'){
                setCreateEventTime(null);
                setTaskTickets(last=> [...last, data]);
                createTaskItem(data);
            }
        }
    }

    const renderCalendarView = () => {
        switch (currentView) {
            case 'day':
                return (
                    <CalendarGridDay
                        baseDate={baseDate}
                        tickets={allTickets}
                        onTimeClick={(time) => setCreateEventTime(time)}
                        onTicketClick={(ticket) => setEditTicket(ticket)}
                    />
                );
            case 'week':
                return (
                    <CalendarGridWeek
                        baseDate={baseDate}
                        tickets={allTickets}
                        columns={7}
                        onTimeClick={(time) => setCreateEventTime(time)}
                        onTicketClick={(ticket) => setEditTicket(ticket)}
                    />
                );
            case 'month':
                const formatMonthYear = (date: Date) => {
                    const options: Intl.DateTimeFormatOptions = {
                        year: 'numeric',
                        month: 'long'
                    };
                    return date.toLocaleDateString(undefined, options);
                };

                return (
                    <div className="calendarGrid_container" style={{"--days-count": daysInMonth.length} as any}>
                        <div className="calendarGridWeek_header">
                            <h2 className="calendarGridWeek_date">{formatMonthYear(baseDate)}</h2>
                        </div>
                        <div className="calendarGrid_scroll_container">
                            {/* Header with days */}
                            <div className="calendarGrid_days_header">
                                <div className="calendarGrid_time_header"></div>
                                {daysInMonth.map((day, index) => (
                                    <div key={index} className="calendarGrid_day_header">
                                        <div className="calendarGrid_day_number">{getTimezoneAwareDate(day)}</div>
                                        <div className="calendarGrid_day_name">
                                            {localizedShortDays[getTimezoneAwareDay(day)]}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Body with time slots */}
                            <div className="calendarGrid_body">
                                {timeSlots.map(hour => {
                                    // Calculate maximum height needed for this row
                                    const maxTicketsInRow = Math.max(
                                        ...daysInMonth.map(day => getTicketsForDayAndHour(day, hour).length),
                                        1
                                    );
                                    const rowHeight = maxTicketsInRow > 1
                                        ? `${2 + (maxTicketsInRow * (25 + 2)) + 5}px`
                                        : '40px';

                                    return (
                                        <div key={hour} className="calendarGrid_time_row" style={{ minHeight: rowHeight }}>
                                            <div className="calendarGrid_time_slot">
                                                {hour.toString().padStart(2, '0')}:00
                                            </div>
                                            {daysInMonth.map((day, dayIndex) => {
                                                const ticketsInSlot = getTicketsForDayAndHour(day, hour);
                                                return (
                                                    <div
                                                        key={dayIndex}
                                                        className="calendarGrid_cell"
                                                        onClick={() => handleTimeClick(day, hour)}
                                                        style={{ minHeight: rowHeight }}
                                                    >
                                                        {ticketsInSlot.map((ticket, ticketIndex) => (
                                                            <CalendarTicket
                                                                key={ticketIndex}
                                                                ticket={ticket}
                                                                ticketIndex={ticketIndex}
                                                                totalTickets={ticketsInSlot.length}
                                                                onTicketClick={() => setEditTicket(ticket)}
                                                            />
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            case 'year':
                return (
                    <CalendarGridYear
                        baseDate={baseDate}
                        tickets={allTickets}
                        onMonthClick={(date) => {
                            setBaseDate(date);
                            setCurrentView('month');
                        }}
                        onDayClick={(date) => {
                            setBaseDate(date);
                            setCurrentView('day');
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return <div className="calendarGrid">
        <div className="calendarGrid_header">
            <CalendarViewSwitcher
                currentView={currentView}
                onViewChange={setCurrentView}
                baseDate={baseDate}
                onDateChange={setBaseDate}
                translate={translate}
            />
        </div>

        {renderCalendarView()}

        {(editTicket || createEventTime) ? <CalendarEventForm
            eventData={editTicket}
            clickedDate={createEventTime}
            onClose={()=>{
                setCreateEventTime(null);
                setEditTicket(null);
            }}
            onDelete={handleDelete}
            onSave={handleSave}
        /> : ''}
    </div>
}